import type { RootState } from '@store';

import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { type ICommonError, POST_MESSAGE_EVENT_TYPE } from '@modules/types';
import { sendPostMessage } from '@utils/sendPostMessage';
import { showErrorToast } from '@utils/showErrorToast';

import { CONFIG, ENV, ROUTES } from '@constants';

const { ACTIVATE_CODE, GIVE_BONUS_MANAGEMENT } = ROUTES;

const baseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      headers.set('X-FE-Version', CONFIG.APP_VERSION);

      const {
        auth: { token },
      } = getState() as RootState;

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      if (
        ENV.VITE_BONUS_SERVICE_X_API_KEY &&
        baseUrl === ENV.VITE_BONUS_SERVICE_API_URL
      ) {
        headers.set('X-API-Key', ENV.VITE_BONUS_SERVICE_X_API_KEY);
      }

      return headers;
    },
  });

type BaseQueryCreator = (
  path: string
) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

export const baseQueryCreator: BaseQueryCreator =
  (path: string) => async (arguments_, api, extraOptions) => {
    const result = await baseQuery(path)(arguments_, api, extraOptions);

    // Description: Handle all errors which does not cover by BE
    if (result?.error) {
      const errorData = result.error.data as ICommonError['data'];
      const isIframe = window.self !== window.top;

      if (!errorData?.traceId) {
        if (
          isIframe &&
          window.location.pathname.includes(GIVE_BONUS_MANAGEMENT.ROOT)
        ) {
          sendPostMessage({
            data: errorData,
            type: POST_MESSAGE_EVENT_TYPE.ERROR_GIVE_BONUS_MODAL,
          });
        }

        if (isIframe && window.location.pathname.includes(ACTIVATE_CODE.ROOT)) {
          sendPostMessage({
            data: errorData,
            type: POST_MESSAGE_EVENT_TYPE.ERROR_ACTIVATE_CODE_MODAL,
          });
        }

        showErrorToast({
          errorData,
          target: 'baseQueryCreator',
          statusCode: String(result.error.status),
        });
      }
    }

    return result;
  };
